import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FileText, Save } from 'lucide-react';
import InfoHint from '../../SharedComponents/InfoHint';
import SettingsSelect from '../SettingsSelect';
import { toast } from 'sonner';
import { useInvoiceSettings } from '../../../../context/InvoiceSettingsContext';

// Invoice settings section — numbering, payment terms, payment methods and
// delivery channels. Self-contained: reads/writes invoice settings straight
// from InvoiceSettingsContext, mirroring the EmailInboxSettings precedent.
// Relies on the global settings styles rendered by the CompanySettings shell.
const PAPER_FORMATS = [{ value: 'A4', label: 'A4' }];

const InvoiceSection = () => {
  const { invoiceSettings, updateInvoiceSetting, saveInvoiceSettings } = useInvoiceSettings();
  const [formErrors, setFormErrors] = useState({});

  const saveInvoiceNumbering = async () => {
    try {
      await saveInvoiceSettings('invoiceNumbering');
      toast.success("Invoice numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save invoice numbering settings");
    }
  };

  const savePaymentTerms = async () => {
    try {
      await saveInvoiceSettings('defaultTerms');
      toast.success("Payment terms saved successfully");
    } catch (error) {
      toast.error("Failed to save payment terms");
    }
  };

  const togglePaymentMethod = (method) => {
    updateInvoiceSetting('paymentMethods', {
      [method]: !invoiceSettings.paymentMethods[method]
    });
  };

  const savePaymentMethods = async () => {
    try {
      await saveInvoiceSettings('paymentMethods');
      toast.success("Payment methods saved successfully");
    } catch (error) {
      toast.error("Failed to save payment methods");
    }
  };

  const toggleDeliveryMethod = (method) => {
    updateInvoiceSetting('deliveryChannels', {
      [method]: !invoiceSettings.deliveryChannels[method]
    });
  };

  const saveDeliveryMethods = async () => {
    try {
      await saveInvoiceSettings('deliveryChannels');
      toast.success("Delivery methods saved successfully");
    } catch (error) {
      toast.error("Failed to save delivery methods");
    }
  };

  return (
    <>
      {/* Invoice Numbering Section */}
      <div className="modern-card" id="fakturanumrering">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <FileText size={16} />
              Fakturanumrering
            </h2>
            <p className="section-subtitle">
              Prefix, startnummer och format för fakturor
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={saveInvoiceNumbering}
            >
              <Save className="me-2" size={16} />
              Spara fakturanumrering
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="invoicePrefix" className="modern-label">
                      Prefix
                    </label>
                    <input
                      type="text"
                      className="modern-input"
                      id="invoicePrefix"
                      placeholder="INV-"
                      value={invoiceSettings.invoiceNumbering.prefix}
                      onChange={(e) => updateInvoiceSetting('invoiceNumbering', { prefix: e.target.value })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="invoiceStartNumber" className="modern-label">
                      Startnummer
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="invoiceStartNumber"
                      placeholder="1001"
                      value={invoiceSettings.invoiceNumbering.startNumber}
                      onChange={(e) => updateInvoiceSetting('invoiceNumbering', {
                        startNumber: parseInt(e.target.value, 10) || 1001
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="invoicePadding" className="modern-label">
                      Antal siffror
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="invoicePadding"
                      min="1"
                      max="10"
                      value={invoiceSettings.invoiceNumbering.padding}
                      onChange={(e) => updateInvoiceSetting('invoiceNumbering', {
                        padding: parseInt(e.target.value, 10) || 4
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="invoiceIncludeYear"
                        checked={invoiceSettings.invoiceNumbering.includeYear}
                        onChange={(e) => updateInvoiceSetting('invoiceNumbering', { includeYear: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="invoiceIncludeYear">
                        Inkludera år i fakturanummer
                      </label>
                    </div>
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="invoiceResetYearly"
                        checked={invoiceSettings.invoiceNumbering.resetYearly}
                        onChange={(e) => updateInvoiceSetting('invoiceNumbering', { resetYearly: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="invoiceResetYearly">
                        Återställ startnummer varje år
                      </label>
                    </div>
                  </div>
                </div>

                {/* Preview for Invoice */}
                <div className="form-field full-width settings-preview">
                  <label className="modern-label">Förhandsgranskning:</label>
                  <div className="settings-preview__value">
                    {invoiceSettings.invoiceNumbering.includeYear
                      ? `${invoiceSettings.invoiceNumbering.prefix}${new Date().getFullYear()}-${String(invoiceSettings.invoiceNumbering.startNumber).padStart(invoiceSettings.invoiceNumbering.padding, '0')}`
                      : `${invoiceSettings.invoiceNumbering.prefix}${String(invoiceSettings.invoiceNumbering.startNumber).padStart(invoiceSettings.invoiceNumbering.padding, '0')}`
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms Section */}
      <div className="modern-card" id="betalningsvillkor">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              Betalningsvillkor
            </h2>
            <p className="section-subtitle">
              Standardbetalningsvillkor och rabatter
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={() => savePaymentTerms()}
            >
              <Save className="me-2" size={16} />
              Spara betalningsvillkor
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="paymentDays" className="modern-label">
                      Standardbetalningsvillkor (dagar)
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="paymentDays"
                      placeholder="30"
                      value={invoiceSettings.defaultTerms.paymentDays}
                      onChange={(e) => updateInvoiceSetting('defaultTerms', {
                        paymentDays: parseInt(e.target.value || '30', 10)
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="lateFeePercentage" className="modern-label">
                      Dröjsmålsränta (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="modern-input"
                      id="lateFeePercentage"
                      placeholder="8.5"
                      value={invoiceSettings.defaultTerms.lateFeePercentage}
                      onChange={(e) => updateInvoiceSetting('defaultTerms', {
                        lateFeePercentage: parseFloat(e.target.value) || 0
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="discountDays" className="modern-label">
                      Rabattperiod (dagar)
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="discountDays"
                      placeholder="10"
                      value={invoiceSettings.defaultTerms.discountDays}
                      onChange={(e) => updateInvoiceSetting('defaultTerms', {
                        discountDays: parseInt(e.target.value, 10) || 0
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="discountPercentage" className="modern-label">
                      Rabatt för förskottsbetalning (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="modern-input"
                      id="discountPercentage"
                      placeholder="2.0"
                      value={invoiceSettings.defaultTerms.discountPercentage}
                      onChange={(e) => updateInvoiceSetting('defaultTerms', {
                        discountPercentage: parseFloat(e.target.value) || 0
                      })}
                    />
                  </div>
                  <div className="form-field full-width">
                    <div className="form-check" style={{ marginTop: "1rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="approvedForFSkatt"
                        checked={invoiceSettings.defaultTerms.fskattApproved}
                        onChange={(e) => updateInvoiceSetting('defaultTerms', { fskattApproved: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="approvedForFSkatt" style={{ marginBottom: 0 }}>
                        Godkänd för F-skatt
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Section */}
      <div className="modern-card" id="betalningsmetoder">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              Betalningsmetoder
            </h2>
            <p className="section-subtitle">
              Konfigurera tillgängliga betalningsmetoder för dina fakturor
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={() => savePaymentMethods()}
            >
              <Save className="me-2" size={16} />
              Spara betalningsmetoder
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="payment-methods-grid">
            {/* Bankgiro */}
            <div className="payment-method-card">
              <div className="payment-method-header">
                <div className="payment-method-info">
                  <h4 className="payment-method-name">Bankgiro</h4>
                </div>
                <div className="feature-toggle">
                  <input
                    className="toggle-input"
                    type="checkbox"
                    id="bankgiroSwitch"
                    checked={invoiceSettings.paymentMethods.bankgiro}
                    onChange={() => togglePaymentMethod('bankgiro')}
                  />
                  <label htmlFor="bankgiroSwitch" className="toggle-label"></label>
                </div>
              </div>
              {invoiceSettings.paymentMethods.bankgiro && (
                <div className="payment-method-config">
                  <div className="form-field">
                    <label htmlFor="bankgiroNumber" className="modern-label">
                      Bankgironummer
                    </label>
                    <input
                      type="text"
                      className="modern-input"
                      id="bankgiroNumber"
                      placeholder="XXXX-XXXX"
                      value={invoiceSettings.paymentMethods.bankgiroNumber}
                      onChange={(e) => updateInvoiceSetting('paymentMethods', {
                        bankgiroNumber: e.target.value
                      })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Plusgiro */}
            <div className="payment-method-card">
              <div className="payment-method-header">
                <div className="payment-method-info">
                  <h4 className="payment-method-name">Plusgiro</h4>
                </div>
                <div className="feature-toggle">
                  <input
                    className="toggle-input"
                    type="checkbox"
                    id="plusgiroSwitch"
                    checked={invoiceSettings.paymentMethods.plusgiro}
                    onChange={() => togglePaymentMethod('plusgiro')}
                  />
                  <label htmlFor="plusgiroSwitch" className="toggle-label"></label>
                </div>
              </div>
              {invoiceSettings.paymentMethods.plusgiro && (
                <div className="payment-method-config">
                  <div className="form-field">
                    <label htmlFor="plusgiroNumber" className="modern-label">
                      Plusgironummer
                    </label>
                    <input
                      type="text"
                      className="modern-input"
                      id="plusgiroNumber"
                      placeholder="XXXXXX-X"
                      value={invoiceSettings.paymentMethods.plusgiroNumber}
                      onChange={(e) => updateInvoiceSetting('paymentMethods', {
                        plusgiroNumber: e.target.value
                      })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* PayPal - Coming Soon */}
            <div className="payment-method-card disabled">
              <div className="payment-method-header">
                <div className="payment-method-info">
                  <h4 className="payment-method-name">
                    PayPal
                    <span className="coming-soon-badge">Kommer snart</span>
                  </h4>
                </div>
              </div>
            </div>

            {/* Swish - Coming Soon */}
            <div className="payment-method-card disabled">
              <div className="payment-method-header">
                <div className="payment-method-info">
                  <h4 className="payment-method-name">
                    Swish
                    <span className="coming-soon-badge">Kommer snart</span>
                  </h4>
                </div>
              </div>
            </div>
          </div>

          <div className="default-method-section">
            <div className="form-field">
              <label htmlFor="defaultPaymentMethod" className="modern-label">
                Förvald betalningsmetod
                <InfoHint text="Välj vilken betalningsmetod som ska vara förvald när du skapar nya fakturor" />
              </label>
              {(() => {
                const options = [
                  invoiceSettings.paymentMethods.bankgiro && { value: 'bankgiro', label: 'Bankgiro' },
                  invoiceSettings.paymentMethods.plusgiro && { value: 'plusgiro', label: 'Plusgiro' },
                ].filter(Boolean);
                return (
                  <SettingsSelect
                    id="defaultPaymentMethod"
                    options={options}
                    value={invoiceSettings.paymentMethods.defaultMethod}
                    onChange={(v) => updateInvoiceSetting('paymentMethods', { defaultMethod: v })}
                    placeholder="Välj betalningsmetod…"
                    noOptionsMessage={() => 'Aktivera en betalningsmetod först'}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Methods Section */}
      <div className="modern-card" id="leveransmetoder">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M4 4l5 5" />
              </svg>
              Leveransmetoder
            </h2>
            <p className="section-subtitle">
              Konfigurera hur fakturor levereras till kunder
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={() => saveDeliveryMethods()}
            >
              <Save className="me-2" size={16} />
              Spara leveransmetoder
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="delivery-methods-grid">
            {/* Email Delivery */}
            <div className="delivery-method-card">
              <div className="delivery-method-header">
                <div className="delivery-method-info">
                  <h4 className="delivery-method-name">E-post</h4>
                </div>
                <div className="feature-toggle">
                  <input
                    className="toggle-input"
                    type="checkbox"
                    id="emailSwitch"
                    checked={invoiceSettings.deliveryChannels.email}
                    onChange={() => toggleDeliveryMethod('email')}
                  />
                  <label htmlFor="emailSwitch" className="toggle-label"></label>
                </div>
              </div>
              {invoiceSettings.deliveryChannels.email && (
                <div className="delivery-method-config">
                  <div className="form-field">
                    <label htmlFor="emailFrom" className="modern-label">
                      Avsändaradress *
                      <InfoHint text="E-postadressen som fakturor skickas från" />
                    </label>
                    <input
                      type="email"
                      className={`modern-input ${formErrors.emailFrom ? 'error' : ''}`}
                      id="emailFrom"
                      placeholder="faktura@dittföretag.se"
                      value={invoiceSettings.deliveryChannels.emailFrom || ""}
                      onChange={(e) => {
                        updateInvoiceSetting('deliveryChannels', {
                          emailFrom: e.target.value
                        });
                        if (formErrors.emailFrom) {
                          setFormErrors({ ...formErrors, emailFrom: null });
                        }
                      }}
                    />
                    {formErrors.emailFrom && (
                      <div className="field-error">
                        {formErrors.emailFrom}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Download Delivery */}
            <div className="delivery-method-card">
              <div className="delivery-method-header">
                <div className="delivery-method-info">
                  <h4 className="delivery-method-name">Nedladdning (PDF)</h4>
                                  </div>
                <div className="feature-toggle">
                  <input
                    className="toggle-input"
                    type="checkbox"
                    id="downloadSwitch"
                    checked={invoiceSettings.deliveryChannels.download}
                    onChange={() => toggleDeliveryMethod('download')}
                  />
                  <label htmlFor="downloadSwitch" className="toggle-label"></label>
                </div>
              </div>
              {invoiceSettings.deliveryChannels.download && (
                <div className="delivery-method-config">
                  <div className="form-field">
                    <label htmlFor="printFormat" className="modern-label">
                      Pappersformat
                    </label>
                    <SettingsSelect
                      id="printFormat"
                      options={PAPER_FORMATS}
                      value={invoiceSettings.deliveryChannels.printFormat || 'A4'}
                      onChange={(v) => updateInvoiceSetting('deliveryChannels', { printFormat: v })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* E-Invoice - Coming Soon */}
            <div className="delivery-method-card disabled">
              <div className="delivery-method-header">
                <div className="delivery-method-info">
                  <h4 className="delivery-method-name">
                    E-faktura
                    <span className="coming-soon-badge">Kommer snart</span>
                  </h4>
                </div>
              </div>
            </div>

            {/* SMS Delivery - Coming Soon */}
            <div className="delivery-method-card disabled">
              <div className="delivery-method-header">
                <div className="delivery-method-info">
                  <h4 className="delivery-method-name">
                    SMS
                    <span className="coming-soon-badge">Kommer snart</span>
                  </h4>
                  <p className="delivery-method-description">Avisering, inte fakturan själv</p>
                </div>
              </div>
            </div>
          </div>

          <div className="default-method-section">
            <div className="form-field">
              <label htmlFor="defaultDeliveryChannel" className="modern-label">
                Förvald leveransmetod
                <InfoHint text="Välj vilken leveransmetod som ska vara förvald när du skapar nya fakturor" />
              </label>
              {(() => {
                const options = [
                  invoiceSettings.deliveryChannels.email && { value: 'email', label: 'E-post' },
                  invoiceSettings.deliveryChannels.download && { value: 'download', label: 'Ladda ner PDF' },
                ].filter(Boolean);
                return (
                  <SettingsSelect
                    id="defaultDeliveryChannel"
                    options={options}
                    value={invoiceSettings.deliveryChannels.defaultChannel}
                    onChange={(v) => updateInvoiceSetting('deliveryChannels', { defaultChannel: v })}
                    placeholder="Välj leveransmetod…"
                    noOptionsMessage={() => 'Aktivera en leveransmetod först'}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceSection;
